import { observer } from "mobx-react-lite";
import { comparer } from "mobx";
import React, { Fragment } from "react";
import { BodyText } from "./BodyText";
import { Button } from "./Button";
import { useFormContext } from "./Form";
import { useTranslation } from "./hooks/useTranslation";
import { classNames } from "./utils/classNames";
import { TreeContext, useTreeContext } from "./hooks/useTreeContext";
import { createUseStyles } from "react-jss";
import { Mode } from "./types";
var TRANSITION = "all 0.2s ease-in-out";
var styles = {
    tree: {
        padding: 0,
        listStyleType: "none",
        margin: 0,
    },
    item: {
        position: "relative",
        height: 32,
        padding: 0,
        borderBottom: {
            width: 1,
            color: "#D5DFE7",
            style: "solid",
        },
        "&:not(:disabled)": {
            "&:hover": {
                transition: TRANSITION,
                background: "#F3F9FC",
                color: "#354551",
                cursor: "pointer",
            },
            "&:active": {
                background: "#F3F9FC",
                color: "#354551",
            },
            "&:focus": {
                background: "#F3F9FC",
                color: "#354551",
                outline: "-webkit-focus-ring-color auto 0px;",
            },
        },
    },
    folderOpen: {
        background: "#1D6DC0",
        color: "#ffffff",
    },
    itemText: {
        flex: 1,
    },
    folder: {
        display: "flex",
        height: 31,
        padding: {
            left: 16,
            right: 8,
            top: 5.5,
            bottom: 5.5,
        },
    },
    nextFolderIcon: {
        width: 10,
        top: 8,
        right: 11,
        position: "absolute",
    },
    checkbox: {
        cursor: "pointer",
        fontWeight: "normal",
        display: "flex",
        margin: 0,
        position: "absolute",
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        "&>input[type='checkbox']": {
            margin: {
                top: 9,
                right: 0,
                bottom: 9,
                left: 15,
            },
        },
    },
    label: {
        flex: 1,
        padding: {
            top: 5.5,
            right: 24,
            bottom: 5.5,
            left: 16,
        },
        overflow: "hidden",
        "& .body-text--message": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
    },
    input: {
        position: "absolute",
        cursor: "pointer",
        visibility: "hidden",
        "&:active + label": {
            backgroundColor: "#F3F9FC",
            color: "#354551",
            transition: TRANSITION,
        },
        "&:focus + label": {
            backgroundColor: "#F3F9FC",
            color: "#354551",
            outline: "-webkit-focus-ring-color auto 0px;",
            transition: TRANSITION,
        },
        "&:focus:checked + label": {
            backgroundColor: "#1C874C",
            color: "#ffffff",
            outline: "-webkit-focus-ring-color auto 0px;",
            transition: TRANSITION,
        },
        "&:checked + label": {
            "& + .more-icon": {
                display: "none",
                transition: TRANSITION,
            },
            "& > span": {
                backgroundColor: "#1C874C",
                color: "#ffffff",
                "& > .glyphicon": {
                    color: "#ffffff",
                },
            },
            "& > span + .glyphicon-pro": {
                display: "block",
                transition: TRANSITION,
            },
        },
    },
    checkedIcon: {
        display: "none",
        width: 20,
        fontSize: 16,
        top: 7,
        right: 2,
        position: "absolute",
        color: "#ffffff",
    },
    empty: {
        color: "#566f80",
        textAlign: "center",
        paddingTop: 16,
    },
    pagination: {
        textAlign: "center",
    },
};
var useStyles = createUseStyles(styles);
export var Tree = function (_a) {
    var children = _a.children, _b = _a.multiple, multiple = _b === void 0 ? false : _b;
    var classes = useStyles();
    return (React.createElement(TreeContext.Provider, { value: {
            multiple: multiple,
        } }, children && (React.createElement("ul", { role: "tree", "aria-multiselectable": multiple, className: classNames(classes.tree, "tree") }, children))));
};
export var TreeItem = function (_a) {
    var label = _a.label, folder = _a.folder, _b = _a.glyph, glyph = _b === void 0 ? "file" : _b, id = _a.id, onChange = _a.onChange, name = _a.name, dataId = _a.dataId, dataLabel = _a.dataLabel, dataTestId = _a.dataTestId;
    var classes = useStyles();
    if (id && name && !folder) {
        return (React.createElement("li", { className: classNames(classes.item), role: "treeitem", "aria-selected": false },
            React.createElement(SelectableItem, { id: id, name: name, label: label, glyph: glyph, onChange: onChange, dataId: dataId, dataLabel: dataLabel, dataTestId: dataTestId })));
    }
    return (React.createElement("li", { className: classNames(classes.item), role: "treeitem", "aria-selected": false, "aria-expanded": folder === "open", tabIndex: !name ? 0 : undefined, onClick: function () { return onChange && onChange(id); }, onKeyPress: function () { return onChange && onChange(id); }, title: label, "data-id": dataId, "data-label": dataLabel, "data-testid": dataTestId },
        React.createElement("span", { className: classNames(classes.folder, folder === "open" ? classes.folderOpen : "") },
            name ? (React.createElement(SelectableItem, { id: id, name: name, label: label, glyph: folder === "open" ? "folderOpen" : "folder", onChange: onChange })) : (React.createElement(BodyText, { message: label, glyph: folder === "open" ? "folderOpen" : "folder", className: classes.itemText, mode: folder === "open" ? Mode.Dark : Mode.Light })),
            folder !== "noChildren" && (React.createElement("span", { className: classNames("more-icon", "glyphicon-pro", "glyphicon-pro-play", classes.nextFolderIcon) })))));
};
var SelectableItem = observer(function (_a) {
    var id = _a.id, glyph = _a.glyph, label = _a.label, onChange = _a.onChange, name = _a.name, dataId = _a.dataId, dataLabel = _a.dataLabel, dataTestId = _a.dataTestId;
    var multiple = useTreeContext().multiple;
    var classes = useStyles();
    var values = useFormContext().values;
    var handleChange = React.useCallback(function (e) {
        var collection = values.get(name) || [];
        if (e.target.checked) {
            var item = {
                value: e.target.name,
                name: label,
            };
            if (multiple) {
                collection.push(item);
            }
            else {
                collection = [item];
            }
        }
        else {
            collection = collection.filter(function (itemValue) {
                return itemValue.value !== e.target.name;
            });
        }
        if (onChange) {
            onChange(collection, values.get(name));
        }
        values.set(name, collection);
    }, [onChange, values, name, label, multiple]);
    var getChecked = function () {
        return !!values
            .get(name)
            .find(function (item) { return comparer.structural(item.value, id); });
    };
    return (React.createElement(Fragment, null,
        React.createElement("input", { name: id, id: id, className: classes.input, type: "checkbox", onChange: handleChange, checked: getChecked(), "data-id": dataId, "data-label": dataLabel, "data-testid": dataTestId }),
        React.createElement("label", { className: classes.checkbox, title: label, htmlFor: id },
            React.createElement(BodyText, { message: label, glyph: glyph, className: classes.label }),
            React.createElement("span", { className: classNames("glyphicon-pro", "glyphicon-pro-circle-empty-check", classes.checkedIcon) }))));
});
export var TreePagination = function (_a) {
    var label = _a.label, onClick = _a.onClick, dataId = _a.dataId, dataLabel = _a.dataLabel, dataTestId = _a.dataTestId;
    var classes = useStyles();
    var t = useTranslation();
    label = label || t("Show more");
    return (React.createElement("div", { className: classes.pagination, "data-id": dataId, "data-label": dataLabel, "data-testid": dataTestId },
        React.createElement(Button, { label: label, onClick: onClick, type: "suppressed" })));
};
export var TreeEmpty = function (_a) {
    var text = _a.text, dataId = _a.dataId, dataLabel = _a.dataLabel, dataTestId = _a.dataTestId;
    var classes = useStyles();
    var t = useTranslation();
    text = text || t("No Results");
    return (React.createElement("p", { className: classes.empty, "data-id": dataId, "data-label": dataLabel, "data-testid": dataTestId }, text));
};
